import React from 'react';
import { number, bool, object, oneOf, string } from 'prop-types';
import { injectIntl, intlShape } from 'react-intl';
import classNames from 'classnames';
import { find, map, path, pathOr } from 'ramda';
import { getPricingDataFromEntity, LINE_ITEM_PROVIDER_INSURANCE } from '@swimmy_/helpers';

import { CUSTOMER, PROVIDER } from '../../constants/booking';
import { types as sdkTypes } from '../../util/sdkLoader';
import { formatMoney } from '../../util/currency';
import { useToggle } from '../../hooks/useToggle';
import { IconExpand, IconReceipt } from '../../components/Icons';

import {
  getCustomerCommissionLineItem,
  getLineItemProps,
  isReversalTransaction,
  isReversalTransactionFromCustomer
} from './PriceBreakdown.helpers';
import LineItem from './LineItem/LineItem';
import css from './PriceBreakdown.module.css';
import { SecondaryButton } from '../Button/Button';

const { Money } = sdkTypes;

const PriceBreakdown = (props) => {
  const { className, expandable, intl, transaction, userRole, proposalPriceAsMoney } = props;

  const invoiceUrl = pathOr(null, ['attributes', 'metadata', 'invoiceUrl'], transaction);

  const displayInvoiceLink = invoiceUrl && userRole === CUSTOMER;

  const [showBreakdown, toggleBreakdown] = useToggle(!expandable);
  const isCustomer = userRole === CUSTOMER;
  const minAmountPerHour = path(['attributes', 'protectedData', 'minAmountPerHour'], transaction);
  const basePricePerHour = path(['attributes', 'protectedData', 'basePricePerHour'], transaction);
  const pricingData = {
    ...getPricingDataFromEntity(transaction),
    ...(minAmountPerHour ? { minAmountPerHour } : {}),
    ...(basePricePerHour ? { basePricePerHour } : {}),
  };
  const isDynamicPricing =
    path(['attributes', 'lineItems', [0], 'unitPrice', 'amount']) < minAmountPerHour;
  const lineItems = pathOr([], ['attributes', 'lineItems'], transaction);
  const isReversal = isReversalTransaction(transaction);
  const isReversalFromCustomer = isReversalTransactionFromCustomer(transaction);
  const payinTotal = pathOr([], ['attributes', 'payinTotal'], transaction);
  const lastTransition = pathOr([], ['attributes', 'lastTransition'], transaction);
  const payoutTotal = pathOr([], ['attributes', 'payoutTotal'], transaction);
  const payinTotalAsMoney = new Money(payinTotal.amount, payinTotal.currency);
  const payoutTotalAsMoney = new Money(payoutTotal.amount, payoutTotal.currency);
  const formattedPayinTotal = formatMoney(intl, payinTotalAsMoney);
  const formattedPayoutTotal = formatMoney(intl, payoutTotalAsMoney);
  const formattedProposalPrice = proposalPriceAsMoney && formatMoney(intl, proposalPriceAsMoney);
  const getFormattedTotal = () => {
    if (!isCustomer) return formattedPayoutTotal;
    if (!isReversal || !isReversalFromCustomer) return formattedPayinTotal;

    const customerCommissionLineItem = getCustomerCommissionLineItem(lineItems);
    return formatMoney(intl, new Money(customerCommissionLineItem.lineTotal.amount, customerCommissionLineItem.lineTotal.currency));
  }
  const formattedTotal = getFormattedTotal();
  const buttonShowText = intl.formatMessage({ id: 'PriceBreakdown.buttonShow' });
  const buttonHideText = intl.formatMessage({ id: 'PriceBreakdown.buttonHide' });
  const buttonText = showBreakdown ? buttonHideText : buttonShowText;
  const titleText = intl.formatMessage({ id: 'PriceBreakdown.title' });
  const proposalLabelText = intl.formatMessage({ id: 'PriceBreakdown.proposalLabel' });
  const totalLabelText = intl.formatMessage({ id: 'PriceBreakdown.totalLabel' });
  const serviceFeeText = intl.formatMessage({ id: 'PriceBreakdown.serviceFee' });
  const serviceFeeInsuranceParticipationText = intl.formatMessage({ id: 'PriceBreakdown.serviceFeeInsuranceParticipation' });
  const dynamicPricingText = intl.formatMessage({ id: 'PriceBreakdown.dynamicPricing' });

  const invoiceText = intl.formatMessage({
    id: `PriceBreakdown.downloadInvoice`,
  });
  const refundedText = intl.formatMessage({ id: `PriceBreakdown.${isCustomer ? (!isReversalFromCustomer ? 'refunded' : 'refundedByCustomer') : 'refundedProvider' }` });
  const classes = classNames(css.root, className);
  const expandButtonClasses = classNames(css.expandButton, {
    [css.expandButtonExpanded]: showBreakdown,
  });
  const iconExpandClasses = classNames(css.iconExpand, { [css.iconExpanded]: showBreakdown });
  const contentClasses = classNames(css.breakdown, { [css.contentExpanded]: showBreakdown });

  if (proposalPriceAsMoney) {
    return (
      <div className={classes}>
        <span className={css.title}>{titleText}</span>
        <LineItem
          className={css.lineItem}
          code="total"
          icon={<IconReceipt />}
          label={proposalLabelText}
          price={formattedProposalPrice}
        />
      </div>
    );
  }

  return (
    <div className={classes}>
      {expandable ? (
        <button className={expandButtonClasses} type="button" onClick={toggleBreakdown}>
          <span className={css.buttonText}>{buttonText}</span>
          <IconExpand className={iconExpandClasses} direction={showBreakdown ? 'up' : 'down'} />
        </button>
      ) : (
        <span className={css.title}>{titleText}</span>
      )}
      {showBreakdown && (
        <div className={contentClasses}>
          {map((lineItem) => {
            const props = getLineItemProps(intl, lineItem, isCustomer, pricingData, isReversal, isReversalFromCustomer);

            return (
              props && (
                <LineItem
                  key={lineItem.code}
                  className={css.lineItem}
                  code={lineItem.code}
                  {...props}
                />
              )
            );
          }, lineItems)}
          <LineItem
            className={css.lineItemTotal}
            code="total"
            icon={<IconReceipt />}
            label={totalLabelText}
            price={formattedTotal}
          />
          <span className={css.serviceFee}>{serviceFeeText}</span>
          {find((lineItem) => lineItem.code === LINE_ITEM_PROVIDER_INSURANCE, lineItems) && !isCustomer && (
            <span className={css.serviceFee}>{serviceFeeInsuranceParticipationText}</span>
          )}
          {isDynamicPricing && <span className={css.dynamicPricing}>{dynamicPricingText}</span>}
        </div>
      )}
      {isReversal && <span className={css.refundInfo}>{refundedText}</span>}
      {displayInvoiceLink ? (
        <SecondaryButton onClick={() => window.open(invoiceUrl)} style={{ marginTop: '20px' }}>
          <span className={css.buttonText}>{invoiceText}</span>
        </SecondaryButton>
      ) : null}
    </div>
  );
};

PriceBreakdown.defaultProps = {
  className: null,
  expandable: false,
};

PriceBreakdown.propTypes = {
  className: string,
  expandable: bool,
  intl: intlShape.isRequired,
  transaction: object.isRequired,
  userRole: oneOf([CUSTOMER, PROVIDER]).isRequired,
  proposalPrice: object,
};

export default injectIntl(PriceBreakdown);
